"use client";

import { useState } from "react";
import { TrendingDown } from "lucide-react";

/* ─────────────────────────────────────────────────────────────
 * Rechner „Klicks nach Position“ (bleibt laut Auftrag 2026-10-28).
 *
 * Klickraten je Position aus einer Branchenstudie zu deutschsprachigen
 * Suchergebnissen (Top-100). Werkzeug- und Anbieternamen werden auf der
 * Seite nicht genannt (Auftrag 1.4). Lead-Annahme: 2 % der organischen
 * Klicks werden zu Anfragen – vereinfachtes Modell, kein Versprechen.
 * ──────────────────────────────────────────────────────────── */
const CTR_BY_POSITION: Record<number, number> = {
  1: 28.5, 2: 15.7, 3: 11.0, 4: 8.0, 5: 7.2,
  6: 5.1, 7: 4.0, 8: 3.2, 9: 2.6, 10: 2.4,
};

export function CtrCalculator() {
  const [searches, setSearches] = useState(2400);
  const [position, setPosition] = useState(7);
  const [aov, setAov] = useState(600);

  const ctrTop = CTR_BY_POSITION[1];
  const ctrNow = CTR_BY_POSITION[position] ?? 0.5;
  const clicksTop = Math.round((searches * ctrTop) / 100);
  const clicksNow = Math.round((searches * ctrNow) / 100);
  const lostClicks = Math.max(0, clicksTop - clicksNow);
  const lostLeads = Math.round(lostClicks * 0.02);
  const lostRevenueYear = lostLeads * aov * 12;

  return (
    <div className="relative overflow-hidden rounded-2xl border p-5 lg:p-8" style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)", boxShadow: "var(--card-shadow-lg)" }}>
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-brand-400/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-12 h-56 w-56 rounded-full bg-rose-400/10 blur-3xl" />

      <div className="relative">
        <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold" style={{ backgroundColor: "var(--fi-error-bg)", color: "var(--fi-error-fg)" }}>
          <TrendingDown className="h-3.5 w-3.5" aria-hidden="true" />
          Rechner
        </span>
        <h2 id="rechner" className="mt-4 text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl" style={{ color: "var(--text)", letterSpacing: "-0.02em" }}>
          Wie viele Klicks verlieren Sie, weil Sie nicht auf Platz 1 stehen?
        </h2>
        <p className="mt-3 text-base leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
          Auf <strong style={{ color: "var(--text)" }}>Platz 1 klicken rund 28,5 %</strong> der Suchenden, auf Platz 7 noch etwa 4 %, auf
          Seite 2 fast niemand. Schieben Sie Ihre aktuelle Position auf den Regler.
        </p>

        <div className="mt-8 grid gap-6 lg:grid-cols-2 lg:gap-10">
          <div className="space-y-6">
            <SliderField id="searches" label="Suchanfragen je Monat (Ihr Suchbegriff)" value={searches} min={100} max={50000} step={100} format={(v) => v.toLocaleString("de-DE")} onChange={setSearches} accent="brand" />
            <SliderField id="position" label="Aktuelle Position bei Google" value={position} min={1} max={10} step={1} format={(v) => `Platz ${v}`} onChange={setPosition} accent="error" />
            <SliderField id="aov" label="Ø Auftragswert je Anfrage" value={aov} min={50} max={5000} step={50} format={(v) => `${v.toLocaleString("de-DE")} €`} onChange={setAov} accent="brand" />
            <div className="rounded-xl border p-4 text-xs leading-relaxed" style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-secondary)", color: "var(--text-tertiary)" }}>
              <p>
                Klickraten je Position aus einer Branchenstudie zu deutschsprachigen Suchergebnissen. Annahme: 2 % der Klicks werden zu
                Anfragen. Vereinfachtes Modell, kein Versprechen.
              </p>
            </div>
          </div>

          <div className="flex flex-col justify-center gap-4 rounded-xl p-5 lg:p-6" style={{ backgroundColor: "var(--bg-secondary)" }}>
            <Row label={`Klickrate auf Platz ${position}`} value={`${ctrNow.toFixed(1).replace(".", ",")} %`} tone="warning" />
            <Row label="Verlorene Klicks je Monat" value={lostClicks.toLocaleString("de-DE")} tone="error" />
            <Row label="Verlorene Anfragen je Monat" value={String(lostLeads)} tone="error" />
            <div className="flex items-baseline justify-between gap-3 border-t pt-3" style={{ borderColor: "var(--border)" }}>
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-tertiary)" }}>Umsatz-Lücke je Jahr</p>
              <p className="text-2xl font-bold tabular-nums sm:text-3xl" style={{ color: "var(--fi-error-fg)" }}>{lostRevenueYear.toLocaleString("de-DE")} €</p>
            </div>
            <div className="mt-3 rounded-lg border-l-4 p-4" style={{ borderColor: "var(--fi-brand-fg)", backgroundColor: "var(--fi-brand-bg)" }}>
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--fi-brand-fg)" }}>Unser Ziel</p>
              <p className="mt-1 text-xl font-bold" style={{ color: "var(--fi-brand-fg)" }}>Seite 1 für Ihre wichtigsten Suchbegriffe – je Ort</p>
              <p className="mt-1 text-xs leading-snug" style={{ color: "var(--fi-brand-fg)" }}>
                Nachhaltige Rankings brauchen Monate, nicht Tage – dafür bleiben sie, anders als jede Anzeige.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, tone }: { label: string; value: string; tone: "warning" | "error" }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-tertiary)" }}>{label}</p>
      <p className="text-xl font-semibold tabular-nums" style={{ color: tone === "error" ? "var(--fi-error-fg)" : "var(--fi-warning-fg)" }}>{value}</p>
    </div>
  );
}

function SliderField({ id, label, value, min, max, step, format, onChange, accent }: { id: string; label: string; value: number; min: number; max: number; step: number; format: (v: number) => string; onChange: (v: number) => void; accent: "brand" | "error" }) {
  const bg = accent === "brand" ? "var(--fi-brand-bg)" : "var(--fi-error-bg)";
  const fg = accent === "brand" ? "var(--fi-brand-fg)" : "var(--fi-error-fg)";
  return (
    <div>
      <div className="flex items-center justify-between">
        <label htmlFor={id} className="text-sm font-semibold" style={{ color: "var(--text)" }}>{label}</label>
        <span className="rounded-md px-2 py-0.5 text-sm font-bold tabular-nums" style={{ backgroundColor: bg, color: fg }}>{format(value)}</span>
      </div>
      <input id={id} type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseInt(e.target.value))} className="mt-2 w-full accent-brand-500" />
      <div className="mt-1 flex justify-between text-xs" style={{ color: "var(--text-tertiary)" }}>
        <span>{format(min)}</span>
        <span>{format(max)}</span>
      </div>
    </div>
  );
}
